import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState, type FormEvent } from 'react'
import { api } from '../lib/api.js'
import { formatValidationDetails } from '../lib/errors.js'
import { PageHeader } from '../components/PageHeader.js'
import { Card, CardFooter } from '../components/Card.js'
import { Button } from '../components/Button.js'
import { Field, Input } from '../components/Field.js'
import {
  StudentForm,
  type StudentFormValues,
  type HistoryDraft,
} from '../components/StudentForm.js'
import { StudentParentSection } from '../components/StudentParentSection.js'
import { FeePlanSection } from '../components/FeePlanSection.js'
import {
  type ParentDraft,
  blankParentDraft,
  missingStudentFields,
} from '../lib/studentTypes.js'
import type { FeePlanInput } from '@academy/types'

export const Route = createFileRoute('/_app/students/new')({
  component: NewStudentPage,
})

interface ClassRow {
  id: string
  name: string
  academicYear: string | null
  isActive: boolean
}

interface StudentListRow {
  id: string
  fullName: string
  classes: { id: string; name: string }[]
  examBoard: string | null
  schoolName: string | null
  isActive: boolean
  deletedAt: string | null
  createdAt: string
}

interface CreateResponse {
  student: {
    id: string
    fullName: string
    examBoard: string | null
    schoolName: string | null
    isActive: boolean
    deletedAt: string | null
    createdAt: string
  }
  classes: { id: string; name: string }[]
  parents: unknown[]
}

function NewStudentPage() {
  const navigate = useNavigate()
  const qc = useQueryClient()

  const classesQuery = useQuery({
    queryKey: ['classes'],
    queryFn: async () => {
      const res = await api<ClassRow[]>('/api/v1/classes')
      if ('error' in res) throw new Error(res.error.message)
      return res.data
    },
  })

  const [values, setValues] = useState<StudentFormValues>({
    fullName: '',
    dateOfBirth: '',
    schoolName: '',
    examBoard: '',
    address: '',
    photoUrl: null,
    specialNotes: '',
  })
  const [selectedClassIds, setSelectedClassIds] = useState<Set<string>>(new Set())
  const [history, setHistory] = useState<HistoryDraft[]>([])
  const [parents, setParents] = useState<ParentDraft[]>([blankParentDraft()])
  const [referredByCode, setReferredByCode] = useState('')
  const [feePlan, setFeePlan] = useState<FeePlanInput | null>(null)
  const [error, setError] = useState<string | null>(null)

  const toggleClass = (id: string) => {
    setSelectedClassIds((curr) => {
      const next = new Set(curr)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const create = useMutation({
    mutationFn: async () => {
      const previousMarks = history
        .filter((h) => h.subject.trim() && h.percentage !== '')
        .map((h) => ({
          subject: h.subject.trim(),
          percentage: parseFloat(h.percentage),
          proofUrl: h.proofUrl ?? undefined,
        }))

      const payload = {
        fullName: values.fullName,
        classIds: Array.from(selectedClassIds),
        dateOfBirth: values.dateOfBirth || undefined,
        schoolName: values.schoolName || undefined,
        examBoard: values.examBoard || undefined,
        address: values.address || undefined,
        photoUrl: values.photoUrl ?? undefined,
        specialNotes: values.specialNotes.trim() || undefined,
        previousMarks: previousMarks.length > 0 ? previousMarks : undefined,
        referredByCode: referredByCode.trim().toUpperCase() || undefined,
        parents: parents.map((p) => ({
          fullName: p.fullName,
          email: p.email,
          phone: p.phone || undefined,
          relationship: p.relationship,
          isPrimary: p.isPrimary,
        })),
        feePlan: feePlan ?? undefined,
      }

      const res = await api<CreateResponse>('/api/v1/students', {
        method: 'POST',
        body: JSON.stringify(payload),
      })
      if ('error' in res) {
        // Prefer the specific field-level reasons ("Date of birth: ...") over the
        // generic "Invalid student payload" so the user knows what to fix.
        const detailed =
          res.error.code === 'VALIDATION_ERROR'
            ? formatValidationDetails(res.error.details)
            : null
        throw new Error(detailed ?? res.error.message)
      }
      return res.data
    },
    onSuccess: (res) => {
      // Seed the returned row into the list cache instead of invalidating
      // (Hyperdrive caching can cause read-after-write reverts on refetch).
      const newRow: StudentListRow = {
        ...res.student,
        classes: res.classes,
      }
      qc.setQueryData<StudentListRow[]>(['students'], (rows) =>
        rows ? [...rows, newRow] : [newRow],
      )
      void navigate({ to: '/students' })
    },
    onError: (err) => setError(err instanceof Error ? err.message : 'Failed'),
  })

  const onSubmit = (e: FormEvent) => {
    e.preventDefault()
    setError(null)
    const missing =
      missingStudentFields(values, selectedClassIds) ??
      (parents.some((p) => !p.phone.trim())
        ? 'A phone number is required for every parent contact.'
        : null)
    if (missing) {
      setError(missing)
      return
    }
    create.mutate()
  }

  return (
    <div className="max-w-3xl space-y-8">
      <PageHeader
        title="Add a student"
        description="The parent will receive an onboarding email and a WhatsApp message with login instructions."
      />

      <form onSubmit={onSubmit} className="space-y-6">
        <StudentForm
          values={values}
          onChange={(patch) => setValues((v) => ({ ...v, ...patch }))}
          classes={classesQuery.data ?? []}
          selectedClassIds={selectedClassIds}
          onToggleClass={toggleClass}
          history={history}
          onHistoryChange={setHistory}
        />

        <Card>
          <CardFooter className="space-y-3">
            <Field
              label="Referred by code"
              optional
              hint="If another parent shared a referred-by code, paste it here. The new family gets 5% off their first invoice."
            >
              <Input
                value={referredByCode}
                onChange={(e) => setReferredByCode(e.target.value)}
                placeholder="e.g. AB23CD45"
                className="font-mono uppercase tracking-wide"
              />
            </Field>
          </CardFooter>
        </Card>

        <StudentParentSection
          mode="create"
          parents={parents}
          onChange={setParents}
        />

        <FeePlanSection onChange={setFeePlan} />

        <Card>
          <CardFooter className="flex items-center justify-between">
            <p className="text-xs text-fg-subtle">
              Notifications are mocked in local dev (logged to the API console).
            </p>
            <div className="flex items-center gap-3">
              {error ? <span className="text-sm text-danger">{error}</span> : null}
              <Button
                type="button"
                variant="ghost"
                onClick={() => void navigate({ to: '/students' })}
              >
                Cancel
              </Button>
              <Button type="submit" loading={create.isPending}>
                Add student
              </Button>
            </div>
          </CardFooter>
        </Card>
      </form>
    </div>
  )
}
